import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import api from "../../api/axios";
import "../../sass/files/freeQuoteLeads.scss";

const FreeQuoteLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");
  const [projectTypeFilter, setProjectTypeFilter] = useState("all");
  const [googleFilter, setGoogleFilter] = useState("all");
  const [clickIdFilter, setClickIdFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [mediumFilter, setMediumFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  const [processingId, setProcessingId] = useState("");
const [
  retryingGoogleId,
  setRetryingGoogleId,
] = useState("");
  const [paymentForms, setPaymentForms] = useState({});

  const [googleStatuses, setGoogleStatuses] = useState({});

  const [checkingGoogleId, setCheckingGoogleId] = useState("");

  const googleStatusesRef = useRef({});

  useEffect(() => {
    googleStatusesRef.current = googleStatuses;
  }, [googleStatuses]);



  const loadLeads = async () => {
    try {
      setLoading(true);

      const response =
        await api.get(
          "/free-quote"
        );

      let data = [];

      if (
        Array.isArray(
          response.data
        )
      ) {
        data =
          response.data;
      } else if (
        Array.isArray(
          response.data
            ?.freeQuotes
        )
      ) {
        data =
          response.data
            .freeQuotes;
      } else if (
        Array.isArray(
          response.data?.data
        )
      ) {
        data =
          response.data.data;
      } else if (
        Array.isArray(
          response.data?.quotes
        )
      ) {
        data =
          response.data.quotes;
      }

      setLeads(data);

      setPaymentForms(
        (previous) => {
          const next = {
            ...previous,
          };

          data.forEach(
            (lead) => {
              if (
                !next[
                  lead._id
                ]
              ) {
                next[
                  lead._id
                ] = {
                  payerEmail:
                    "",

                  paymentAmount:
                    "",

                  paymentCurrency:
                    lead.paymentCurrency ||
                    "USD",
                };
              }
            }
          );

          return next;
        }
      );
    } catch (error) {
      console.error(
        "Failed to load free quotes:",
        error.response
          ?.data ||
          error
      );

      alert(
        "Could not load guest leads."
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // GOOGLE STATUS CHECK
  // =====================================================

  const checkGoogleStatus =
    async (
      lead,
      silent = false
    ) => {
      if (
        !lead
          ?.googleConversionRequestId
      ) {
        return;
      }

      try {
        if (!silent) {
          setCheckingGoogleId(
            lead._id
          );
        }

        const response =
          await api.get(
            `/free-quote/${lead._id}/google-status`
          );

        const status =
          response.data
            ?.googleStatus ||
          "UNKNOWN";

        const result = {
          status,

          errorInfo:
            response.data
              ?.errorInfo ||
            null,

          warningInfo:
            response.data
              ?.warningInfo ||
            null,

          eventsIngestionStatus:
            response.data
              ?.eventsIngestionStatus ||
            null,

          checkedAt:
            new Date(),
        };

        setGoogleStatuses(
          (previous) => ({
            ...previous,

            [lead._id]:
              result,
          })
        );

        googleStatusesRef.current =
          {
            ...googleStatusesRef.current,

            [lead._id]:
              result,
          };
      } catch (error) {
        console.error(
          "Google status check failed:",
          error.response
            ?.data ||
            error
        );

        const result = {
          status:
            "CHECK_FAILED",

          errorInfo:
            error.response
              ?.data
              ?.error ||
            error.response
              ?.data
              ?.message ||
            error.message,

          checkedAt:
            new Date(),
        };

        setGoogleStatuses(
          (previous) => ({
            ...previous,

            [lead._id]:
              result,
          })
        );

        googleStatusesRef.current =
          {
            ...googleStatusesRef.current,

            [lead._id]:
              result,
          };
      } finally {
        if (!silent) {
          setCheckingGoogleId(
            ""
          );
        }
      }
    };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    loadLeads();
  }, []);

  // =====================================================
  // AUTOMATIC GOOGLE STATUS POLLING
  // =====================================================

  useEffect(() => {
    const eligibleLeads =
      leads.filter(
        (lead) =>
          lead.paymentStatus ===
            "paid" &&
          Boolean(
            lead.googleConversionRequestId
          )
      );

    if (
      eligibleLeads.length ===
      0
    ) {
      return;
    }

    /*
     * Check immediately when
     * paid leads are loaded.
     */
    eligibleLeads.forEach(
      (lead) => {
        const existing =
          googleStatusesRef
            .current[
            lead._id
          ]?.status;

        if (
          existing !==
            "SUCCESS" &&
          existing !==
            "FAILED" &&
          existing !==
            "PARTIAL_SUCCESS"
        ) {
          checkGoogleStatus(
            lead,
            true
          );
        }
      }
    );

    /*
     * Recheck every 60 seconds.
     */
    const interval =
      setInterval(() => {
        eligibleLeads.forEach(
          (lead) => {
            const status =
              googleStatusesRef
                .current[
                lead._id
              ]?.status;

            /*
             * Stop checking once
             * Google has returned
             * a final result.
             */
            if (
              status ===
                "SUCCESS" ||
              status ===
                "FAILED" ||
              status ===
                "PARTIAL_SUCCESS"
            ) {
              return;
            }

            checkGoogleStatus(
              lead,
              true
            );
          }
        );
      }, 60000);

    return () => {
      clearInterval(
        interval
      );
    };
  }, [leads]);

  // =====================================================
  // PAYMENT FORM UPDATE
  // =====================================================

  const updatePaymentField = (
    leadId,
    field,
    value
  ) => {
    setPaymentForms(
      (previous) => ({
        ...previous,

        [leadId]: {
          ...(
            previous[
              leadId
            ] || {}
          ),

          [field]:
            value,
        },
      })
    );
  };

  // =====================================================
  // NORMALIZE EMAIL
  // =====================================================

  const normalizeEmail = (
    email
  ) =>
    String(
      email || ""
    )
      .trim()
      .toLowerCase();

  // =====================================================
  // MARK AS PAID
  // =====================================================

  const markAsPaid =
    async (lead) => {
      const form =
        paymentForms[
          lead._id
        ] || {};

      const payerEmail =
        normalizeEmail(
          form.payerEmail
        );

      const leadEmail =
        normalizeEmail(
          lead.emailId
        );

      if (!payerEmail) {
        alert(
          "Enter the email used for the payment."
        );

        return;
      }

      if (
        payerEmail !==
        leadEmail
      ) {
        alert(
          `Email does not match.\n\n` +
            `Guest lead email: ${lead.emailId}\n` +
            `Payment email: ${form.payerEmail}`
        );

        return;
      }

      const amount =
        Number(
          form.paymentAmount
        );

      if (
        !amount ||
        Number.isNaN(
          amount
        ) ||
        amount <= 0
      ) {
        alert(
          "Enter a valid payment amount."
        );

        return;
      }

      const currency =
        form.paymentCurrency ||
        "USD";

      const confirmed =
        window.confirm(
          `Confirm real payment?\n\n` +
            `Customer: ${lead.name}\n` +
            `Email: ${lead.emailId}\n` +
            `Amount: ${currency} ${amount}\n\n` +
            `This will mark the lead as paid and submit the conversion to Google Ads.`
        );

      if (!confirmed) {
        return;
      }

      try {
        setProcessingId(
          lead._id
        );

        const response =
          await api.patch(
            `/free-quote/${lead._id}/mark-paid`,
            {
              payerEmail,

              paymentAmount:
                amount,

              paymentCurrency:
                currency,

              validateOnly:
                false,
            }
          );

        if (
          response.data
            ?.googleUploaded
        ) {
          alert(
            "Payment saved and conversion submitted to Google Ads."
          );
        } else if (
          response.data
            ?.paymentSaved
        ) {
          alert(
            "Payment was saved, but Google Ads upload was not completed."
          );
        } else {
          alert(
            response.data
              ?.message ||
              "Completed."
          );
        }

        await loadLeads();
      } catch (error) {
        console.error(
          "Mark paid error:",
          error.response
            ?.data ||
            error
        );

        alert(
          error.response
            ?.data
            ?.message ||
            "Failed to mark customer as paid."
        );
      } finally {
        setProcessingId(
          ""
        );
      }
    };

  // =====================================================
  // FILTER LEADS
  // =====================================================

  const projectTypeOptions = useMemo(
    () => Array.from(new Set(leads.map((lead) => lead.projectType).filter(Boolean))).sort(),
    [leads]
  );

  const sourceOptions = useMemo(
    () => Array.from(new Set(leads.map((lead) => lead.utmSource).filter(Boolean))).sort(),
    [leads]
  );

  const mediumOptions = useMemo(
    () => Array.from(new Set(leads.map((lead) => lead.utmMedium).filter(Boolean))).sort(),
    [leads]
  );

  const campaignOptions = useMemo(
    () => Array.from(new Set(leads.map((lead) => lead.utmCampaign).filter(Boolean))).sort(),
    [leads]
  );

  const currencyOptions = useMemo(
    () => Array.from(new Set(leads.map((lead) => lead.paymentCurrency).filter(Boolean))).sort(),
    [leads]
  );

  const resetFilters = () => {
    setSearch("");
    setPaymentFilter("all");
    setLeadStatusFilter("all");
    setProjectTypeFilter("all");
    setGoogleFilter("all");
    setClickIdFilter("all");
    setSourceFilter("all");
    setMediumFilter("all");
    setCampaignFilter("all");
    setCurrencyFilter("all");
    setDateFrom("");
    setDateTo("");
    setSortOrder("newest");
  };

  const filteredLeads = useMemo(() => {
    const query = search.trim().toLowerCase();

    const result = leads.filter((lead) => {
      const searchableValues = [
        lead.name,
        lead.emailId,
        lead.whatsapp,
        lead.projectType,
        lead.title,
        lead.paymentStatus,
        lead.status,
        lead.utmSource,
        lead.utmCampaign,
        lead.utmTerm,
        lead.gclid,
        lead.gbraid,
        lead.wbraid,
      ];

      if (
        query &&
        !searchableValues.some((value) =>
          String(value || "").toLowerCase().includes(query)
        )
      ) {
        return false;
      }

      if (paymentFilter !== "all") {
        const paymentStatus = lead.paymentStatus || "unpaid";
        if (paymentStatus !== paymentFilter) return false;
      }

      if (leadStatusFilter !== "all" && (lead.status || "new") !== leadStatusFilter) {
        return false;
      }

      if (projectTypeFilter !== "all" && lead.projectType !== projectTypeFilter) {
        return false;
      }

      const hasClickId = Boolean(lead.gclid || lead.gbraid || lead.wbraid);
      if (clickIdFilter === "captured" && !hasClickId) return false;
      if (clickIdFilter === "missing" && hasClickId) return false;

      if (sourceFilter !== "all" && lead.utmSource !== sourceFilter) return false;
      if (mediumFilter !== "all" && lead.utmMedium !== mediumFilter) return false;
      if (campaignFilter !== "all" && lead.utmCampaign !== campaignFilter) return false;
      if (currencyFilter !== "all" && lead.paymentCurrency !== currencyFilter) return false;

      if (googleFilter !== "all") {
        const currentGoogleStatus = String(
          googleStatuses[lead._id]?.status ||
            lead.googleConversionStatus ||
            (lead.googleConversionUploaded ? "success" : "not_sent")
        ).toLowerCase();

        if (googleFilter === "uploaded" && !lead.googleConversionUploaded) return false;
        if (googleFilter !== "uploaded" && currentGoogleStatus !== googleFilter) return false;
      }

      if (dateFrom || dateTo) {
        const createdAt = lead.createdAt ? new Date(lead.createdAt) : null;
        if (!createdAt || Number.isNaN(createdAt.getTime())) return false;

        if (dateFrom) {
          const start = new Date(`${dateFrom}T00:00:00`);
          if (createdAt < start) return false;
        }

        if (dateTo) {
          const end = new Date(`${dateTo}T23:59:59.999`);
          if (createdAt > end) return false;
        }
      }

      return true;
    });

    return [...result].sort((a, b) => {
      if (sortOrder === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      }

      if (sortOrder === "amount-high") {
        return Number(b.paymentAmount || 0) - Number(a.paymentAmount || 0);
      }

      if (sortOrder === "amount-low") {
        return Number(a.paymentAmount || 0) - Number(b.paymentAmount || 0);
      }

      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [
    leads,
    search,
    paymentFilter,
    leadStatusFilter,
    projectTypeFilter,
    googleFilter,
    clickIdFilter,
    sourceFilter,
    mediumFilter,
    campaignFilter,
    currencyFilter,
    dateFrom,
    dateTo,
    sortOrder,
    googleStatuses,
  ]);

  // =====================================================
  // GOOGLE STATUS LABEL
  // =====================================================
const retryGoogleUpload = async (lead) => {
  const confirmed = window.confirm(
    `Retry Google Ads conversion?\n\n` +
      `Customer: ${lead.name}\n` +
      `Payment: ${lead.paymentCurrency || "USD"} ${lead.paymentAmount}\n\n` +
      `The payment will NOT be changed. Only the Google Ads conversion will be submitted.`
  );

  if (!confirmed) {
    return;
  }

  try {
    setRetryingGoogleId(lead._id);

    const response = await api.post(
      `/free-quote/${lead._id}/retry-google`
    );

    alert(
      response.data?.message ||
        "Google Ads conversion submitted."
    );

    await loadLeads();
  } catch (error) {
    console.error(
      "Google conversion retry failed:",
      error.response?.data || error
    );

    alert(
      error.response?.data?.message ||
        "Google Ads conversion retry failed."
    );
  } finally {
    setRetryingGoogleId("");
  }
};
  const getGoogleStatus =
    (lead) => {
      const liveStatus =
        googleStatuses[
          lead._id
        ]?.status;

      if (liveStatus) {
        return liveStatus;
      }

      if (
        lead.googleConversionRequestId
      ) {
        return String(
          lead.googleConversionStatus ||
            "PROCESSING"
        ).toUpperCase();
      }

      if (
        lead.googleConversionUploaded
      ) {
        return "SUBMITTED";
      }

      return "NOT_SENT";
    };

  // =====================================================
  // GOOGLE BADGE
  // =====================================================

  const renderGoogleBadge =
    (status) => {
      switch (status) {
        case "SUCCESS":
          return (
            <strong className="google-status-badge google-success">
              ✓ Success
            </strong>
          );

        case "PROCESSING":
          return (
            <strong className="google-status-badge google-processing">
              ⏳ Processing
            </strong>
          );

        case "FAILED":
          return (
            <strong className="google-status-badge google-failed">
              ✕ Failed
            </strong>
          );

        case "PARTIAL_SUCCESS":
          return (
            <strong className="google-status-badge google-partial-success">
              ⚠ Partial Success
            </strong>
          );

        case "CHECK_FAILED":
          return (
            <strong className="google-status-badge google-failed">
              Status Check Failed
            </strong>
          );

        case "SUBMITTED":
          return (
            <strong className="google-status-badge google-submitted">
              Submitted
            </strong>
          );

        case "NOT_SENT":
          return (
            <strong className="google-status-badge google-not-sent">
              Not Sent
            </strong>
          );

        default:
          return (
            <strong className="google-status-badge google-not-sent">
              {status}
            </strong>
          );
      }
    };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="freequote-admin-loading">
        Loading guest
        leads...
      </div>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div className="freequote-admin-page">
      {/* HEADER */}

      <div className="freequote-admin-header">
        <div>
          <h1>
            Guest Leads
          </h1>

          <p>
            Match the
            payment email
            with the
            original guest
            request before
            sending the paid
            conversion to
            Google Ads.
          </p>
        </div>

        <div className="freequote-admin-actions">
          <input
            type="text"
            placeholder="Search name, email or project..."
            value={
              search
            }
            onChange={(
              event
            ) =>
              setSearch(
                event.target
                  .value
              )
            }
          />

          <button
            type="button"
            onClick={
              loadLeads
            }
            className="refresh-leads-btn"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="freequote-filter-panel">
        <div className="freequote-filter-grid">
          <label>
            <span>Payment</span>
            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="all">All payments</option>
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="refunded">Refunded</option>
            </select>
          </label>

          <label>
            <span>Lead status</span>
            <select value={leadStatusFilter} onChange={(e) => setLeadStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>

          <label>
            <span>Project type</span>
            <select value={projectTypeFilter} onChange={(e) => setProjectTypeFilter(e.target.value)}>
              <option value="all">All project types</option>
              {projectTypeOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Google conversion</span>
            <select value={googleFilter} onChange={(e) => setGoogleFilter(e.target.value)}>
              <option value="all">All Google statuses</option>
              <option value="not_sent">Not sent</option>
              <option value="processing">Processing</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="uploaded">Uploaded</option>
            </select>
          </label>

          <label>
            <span>Click ID</span>
            <select value={clickIdFilter} onChange={(e) => setClickIdFilter(e.target.value)}>
              <option value="all">All attribution</option>
              <option value="captured">GCLID / GBRAID / WBRAID captured</option>
              <option value="missing">Click ID missing</option>
            </select>
          </label>

          <label>
            <span>UTM source</span>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="all">All sources</option>
              {sourceOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>

          <label>
            <span>UTM medium</span>
            <select value={mediumFilter} onChange={(e) => setMediumFilter(e.target.value)}>
              <option value="all">All mediums</option>
              {mediumOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>

          <label>
            <span>UTM campaign</span>
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}>
              <option value="all">All campaigns</option>
              {campaignOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>

          <label>
            <span>Currency</span>
            <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)}>
              <option value="all">All currencies</option>
              {currencyOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>

          <label>
            <span>From date</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>

          <label>
            <span>To date</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>

          <label>
            <span>Sort</span>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="amount-high">Payment amount: high to low</option>
              <option value="amount-low">Payment amount: low to high</option>
            </select>
          </label>
        </div>

        <div className="freequote-filter-footer">
          <span>{filteredLeads.length} of {leads.length} leads shown</span>
          <button type="button" onClick={resetFilters}>Reset filters</button>
        </div>
      </div>

      {/* SUMMARY */}

      <div className="freequote-summary">
        <div className="summary-box">
          <span>
            Total Leads
          </span>

          <strong>
            {
              leads.length
            }
          </strong>
        </div>

        <div className="summary-box">
          <span>
            Unpaid
          </span>

          <strong>
            {
              leads.filter(
                (lead) =>
                  lead.paymentStatus !==
                  "paid"
              ).length
            }
          </strong>
        </div>

        <div className="summary-box">
          <span>
            Paid
          </span>

          <strong>
            {
              leads.filter(
                (lead) =>
                  lead.paymentStatus ===
                  "paid"
              ).length
            }
          </strong>
        </div>

        <div className="summary-box">
          <span>
            Google Uploaded
          </span>

          <strong>
            {
              leads.filter(
                (lead) =>
                  lead.googleConversionUploaded
              ).length
            }
          </strong>
        </div>
      </div>

      {/* EMPTY */}

      {filteredLeads.length ===
      0 ? (
        <div className="freequote-empty">
          No guest leads
          found.
        </div>
      ) : (
        <div className="freequote-leads-grid">
          {filteredLeads.map(
            (lead) => {
              const paid =
                lead.paymentStatus ===
                "paid";

              const paymentForm =
                paymentForms[
                  lead._id
                ] || {};

              const hasClickId =
                Boolean(
                  lead.gclid ||
                    lead.gbraid ||
                    lead.wbraid
                );

              const googleStatus =
                getGoogleStatus(
                  lead
                );

              const googleInfo =
                googleStatuses[
                  lead._id
                ];

              return (
                <div
                  className={`freequote-lead-card ${
                    paid
                      ? "is-paid"
                      : ""
                  }`}
                  key={
                    lead._id
                  }
                >
                  {/* TOP */}

                  <div className="lead-card-top">
                    <div>
                      <h2>
                        {lead.name ||
                          "Guest Lead"}
                      </h2>

                      <span className="lead-date">
                        {lead.createdAt
                          ? new Date(
                              lead.createdAt
                            ).toLocaleString()
                          : ""}
                      </span>
                    </div>

                    <span
                      className={`payment-badge ${
                        paid
                          ? "paid"
                          : "unpaid"
                      }`}
                    >
                      {paid
                        ? "PAID"
                        : "UNPAID"}
                    </span>
                  </div>

                  {/* LEAD INFO */}

                  <div className="lead-information">
                    <div>
                      <span>
                        Email
                      </span>

                      <strong>
                        {lead.emailId ||
                          "—"}
                      </strong>
                    </div>

                    <div>
                      <span>
                        WhatsApp
                      </span>

                      <strong>
                        {lead.whatsapp ||
                          "—"}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Project
                        Type
                      </span>

                      <strong>
                        {lead.projectType ||
                          "—"}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Project
                      </span>

                      <strong>
                        {lead.title ||
                          "—"}
                      </strong>
                    </div>
                  </div>

                  {/* ATTRIBUTION */}

                  <div className="lead-attribution-box">
                    <div>
                      <span>
                        Google
                        Click ID
                      </span>

                      <strong
                        className={
                          hasClickId
                            ? "status-good"
                            : "status-neutral"
                        }
                      >
                        {hasClickId
                          ? "Captured"
                          : "Not captured"}
                      </strong>
                    </div>

                    <div>
                      <span>
                        GCLID
                      </span>

                      <code>
                        {lead.gclid
                          ? `${lead.gclid.substring(
                              0,
                              20
                            )}...`
                          : "—"}
                      </code>
                    </div>

                    <div>
                      <span>
                        Campaign
                      </span>

                      <strong>
                        {lead.utmCampaign ||
                          "—"}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Source
                      </span>

                      <strong>
                        {lead.utmSource ||
                          "—"}
                      </strong>
                    </div>
                  </div>

                  {/* DESCRIPTION */}

                  {lead.notes && (
                    <div className="lead-notes">
                      <span>
                        Description
                      </span>

                      <p>
                        {
                          lead.notes
                        }
                      </p>
                    </div>
                  )}

                  {/* PAID */}

                  {paid ? (
                    <div className="paid-information">
                      <div>
                        <span>
                          Payment
                        </span>

                        <strong>
                          {lead.paymentCurrency ||
                            "USD"}{" "}
                          {
                            lead.paymentAmount
                          }
                        </strong>
                      </div>

                      <div>
                        <span>
                          Paid At
                        </span>

                        <strong>
                          {lead.paidAt
                            ? new Date(
                                lead.paidAt
                              ).toLocaleString()
                            : "—"}
                        </strong>
                      </div>

                      {/* GOOGLE STATUS */}

                      <div className="google-status-section">
                        <span>
                          Google Ads
                        </span>

                        {renderGoogleBadge(
                          googleStatus
                        )}

                        {lead.googleConversionRequestId && (
                          <button
                            type="button"
                            className="google-status-refresh-btn"
                            disabled={
                              checkingGoogleId ===
                              lead._id
                            }
                            onClick={() =>
                              checkGoogleStatus(
                                lead
                              )
                            }
                          >
                            {checkingGoogleId ===
                            lead._id
                              ? "Checking..."
                              : "Check now"}
                          </button>
                        )}

                        {!lead.googleConversionRequestId &&
                          googleStatus === "NOT_SENT" && (
                            <button
                              type="button"
                              className="google-status-refresh-btn"
                              disabled={
                                retryingGoogleId ===
                                lead._id
                              }
                              onClick={() =>
                                retryGoogleUpload(
                                  lead
                                )
                              }
                            >
                              {retryingGoogleId ===
                              lead._id
                                ? "Submitting..."
                                : "Retry Google Upload"}
                            </button>
                          )}

                        {googleInfo?.checkedAt && (
                          <small className="google-last-checked">
                            Last
                            checked{" "}
                            {new Date(
                              googleInfo.checkedAt
                            ).toLocaleTimeString()}
                          </small>
                        )}

                        {lead.googleConversionRequestId && (
                          <small className="google-request-id">
                            Request:{" "}
                            {
                              lead.googleConversionRequestId
                            }
                          </small>
                        )}
                      </div>

                      {/* GOOGLE ERROR */}

                      {googleInfo?.errorInfo && (
                        <div className="google-error-message">
                          <strong>
                            Google
                            error:
                          </strong>{" "}
                          {typeof googleInfo.errorInfo ===
                          "string"
                            ? googleInfo.errorInfo
                            : JSON.stringify(
                                googleInfo.errorInfo
                              )}
                        </div>
                      )}

                      {/* GOOGLE WARNING */}

                      {googleInfo?.warningInfo && (
                        <div className="google-warning-message">
                          <strong>
                            Google
                            warning:
                          </strong>{" "}
                          {typeof googleInfo.warningInfo ===
                          "string"
                            ? googleInfo.warningInfo
                            : JSON.stringify(
                                googleInfo.warningInfo
                              )}
                        </div>
                      )}

                      {lead.googleConversionError &&
                        !googleInfo?.errorInfo && (
                          <div className="google-error-message">
                            {
                              lead.googleConversionError
                            }
                          </div>
                        )}
                    </div>
                  ) : (
                    /* UNPAID */

                    <div className="payment-entry-box">
                      <h3>
                        Confirm
                        Invoice
                        Payment
                      </h3>

                      <p>
                        Enter the
                        email shown
                        on the paid
                        invoice. It
                        must match
                        the original
                        guest lead
                        email.
                      </p>

                      <div className="payment-fields">
                        <div className="payment-field email-field">
                          <label>
                            Payment /
                            Invoice
                            Email
                          </label>

                          <input
                            type="email"
                            placeholder={
                              lead.emailId
                            }
                            value={
                              paymentForm.payerEmail ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              updatePaymentField(
                                lead._id,
                                "payerEmail",
                                event
                                  .target
                                  .value
                              )
                            }
                          />
                        </div>

                        <div className="payment-field">
                          <label>
                            Amount
                            Paid
                          </label>

                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="250"
                            value={
                              paymentForm.paymentAmount ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              updatePaymentField(
                                lead._id,
                                "paymentAmount",
                                event
                                  .target
                                  .value
                              )
                            }
                          />
                        </div>

                        <div className="payment-field currency-field">
                          <label>
                            Currency
                          </label>

                          <select
                            value={
                              paymentForm.paymentCurrency ||
                              "USD"
                            }
                            onChange={(
                              event
                            ) =>
                              updatePaymentField(
                                lead._id,
                                "paymentCurrency",
                                event
                                  .target
                                  .value
                              )
                            }
                          >
                            <option value="USD">
                              USD
                            </option>

                            <option value="EUR">
                              EUR
                            </option>

                            <option value="GBP">
                              GBP
                            </option>

                            <option value="INR">
                              INR
                            </option>

                            <option value="CAD">
                              CAD
                            </option>

                            <option value="AUD">
                              AUD
                            </option>
                          </select>
                        </div>
                      </div>

                      <button
                        type="button"
                        className="mark-paid-btn"
                        disabled={
                          processingId ===
                          lead._id
                        }
                        onClick={() =>
                          markAsPaid(
                            lead
                          )
                        }
                      >
                        {processingId ===
                        lead._id
                          ? "Submitting..."
                          : "Mark as Paid"}
                      </button>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      )}
    </div>
  );
};

export default FreeQuoteLeads;